import { useEffect, useState } from 'react';
import { Box, Chip, MenuItem, TextField, Tooltip } from '@mui/material';
import { useFeature } from '../admin/featureFlags/FeatureGate';
import { FEATURE_PRE_ORDER_ATTACHMENTS, fetchAttachments, type PreOrderAttachment } from './preOrderAttachmentsApi';

/**
 * „Źródło ustalenia” przy ręcznej poprawce prezlecenia (paczka p. Mariusza
 * 21.10, BR-CS-14 w części bez poczty; przełącznik `preOrderAgreementSource`,
 * S-21.10-15; API `pre-order/agreement-source.ts`): skąd CS wie o zmianie,
 * gdy system tego nie widzi. Rodzaje to dane z API.
 */
export const FEATURE_PRE_ORDER_AGREEMENT_SOURCE = 'preOrderAgreementSource';

export const AGREEMENT_SOURCE_CHOICES: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'phone', name: 'telefon' },
  { id: 'email', name: 'mail poza systemem' },
  { id: 'verbal', name: 'ustalenie ustne' },
  { id: 'other', name: 'inne' },
];

/**
 * Plik od klienta dołączony do prezlecenia (26.10.2026, `preOrderAttachments`,
 * S-26.10-13) — rodzaj tylko z przełącznikiem plików i przy prezleceniu z plikiem.
 */
export const ATTACHMENT_SOURCE = { id: 'attachment', name: 'plik od klienta' } as const;

/** Etykieta rodzaju — także „plik od klienta”. */
export const agreementSourceKindLabel = (kind: string): string =>
  [...AGREEMENT_SOURCE_CHOICES, ATTACHMENT_SOURCE].find((c) => c.id === kind)?.name ?? kind;

/** Najkrótszy opis przy „inne” — jak w API. */
export const MIN_OTHER_NOTE = 5;

export interface AgreementSourceInput {
  kind: string;
  note?: string;
  /** Plik przy prezleceniu — przy rodzaju „plik od klienta”. */
  attachmentId?: number;
}

/** Czy źródło da się zapisać: brak — tak; „inne” — z opisem; plik — wskazany. */
export function agreementSourceReady(s: AgreementSourceInput | null | undefined): boolean {
  if (!s || !s.kind) return true;
  if (s.kind === ATTACHMENT_SOURCE.id) return typeof s.attachmentId === 'number';
  return s.kind !== 'other' || (s.note ?? '').trim().length >= MIN_OTHER_NOTE;
}

/** Żywe pliki prezlecenia do wyboru źródła — tylko z przełącznikiem plików. */
function useLiveAttachments(preOrderId: number | string | undefined, on: boolean): PreOrderAttachment[] {
  const [files, setFiles] = useState<PreOrderAttachment[]>([]);
  useEffect(() => {
    if (!on || preOrderId === undefined) return;
    let alive = true;
    fetchAttachments(preOrderId)
      .then((l) => {
        if (alive) setFiles(l.attachments.filter((a) => a.deletedAt === null));
      })
      .catch(() => {
        if (alive) setFiles([]);
      });
    return () => {
      alive = false;
    };
  }, [preOrderId, on]);
  return files;
}

/** Pola formularza: rodzaj i opis; puste = bez źródła (zapis jak dotąd). */
export const AgreementSourceFields = ({
  value,
  onChange,
  disabled = false,
  preOrderId,
}: {
  value: AgreementSourceInput | null | undefined;
  onChange: (v: AgreementSourceInput | null) => void;
  disabled?: boolean;
  /** Prezlecenie — do wyboru pliku od klienta jako źródła (26.10.2026). */
  preOrderId?: number | string;
}) => {
  const on = useFeature(FEATURE_PRE_ORDER_AGREEMENT_SOURCE);
  const filesOn = useFeature(FEATURE_PRE_ORDER_ATTACHMENTS);
  const files = useLiveAttachments(preOrderId, on && filesOn);
  if (!on) return null;
  const kind = value?.kind ?? '';
  const note = value?.note ?? '';
  const attachmentId = value?.attachmentId;
  const fileKind = kind === ATTACHMENT_SOURCE.id;
  const needsNote = kind === 'other' && note.trim().length < MIN_OTHER_NOTE;
  return (
    <Box
      sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))' }}
      data-testid="pre-order-agreement-source"
    >
      <TextField
        select
        size="small"
        label="Źródło ustalenia"
        value={kind}
        disabled={disabled}
        onChange={(e) =>
          onChange(
            e.target.value
              ? {
                  kind: e.target.value,
                  note,
                  // Jeden plik przy prezleceniu — wskazany od razu.
                  ...(e.target.value === ATTACHMENT_SOURCE.id && files.length === 1 ? { attachmentId: files[0].id } : {}),
                }
              : null,
          )
        }
        helperText="Skąd wiesz o zmianie — trafi do historii każdego zmienionego pola"
        slotProps={{ htmlInput: { 'data-testid': 'pre-order-agreement-source-kind' } }}
      >
        <MenuItem value="">— bez źródła —</MenuItem>
        {AGREEMENT_SOURCE_CHOICES.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.name}
          </MenuItem>
        ))}
        {files.length > 0 || fileKind ? <MenuItem value={ATTACHMENT_SOURCE.id}>{ATTACHMENT_SOURCE.name}</MenuItem> : null}
      </TextField>
      {fileKind ? (
        <TextField
          select
          size="small"
          label="Plik"
          value={attachmentId ?? ''}
          disabled={disabled}
          error={attachmentId === undefined}
          onChange={(e) => onChange({ kind, note, attachmentId: Number(e.target.value) })}
          helperText="Który plik od klienta mówi o zmianie"
          slotProps={{ htmlInput: { 'data-testid': 'pre-order-agreement-source-file' } }}
        >
          {files.map((f) => (
            <MenuItem key={f.id} value={f.id}>
              {f.filename}
            </MenuItem>
          ))}
        </TextField>
      ) : null}
      <TextField
        size="small"
        label="Opis źródła"
        value={note}
        disabled={disabled || kind === ''}
        error={needsNote}
        onChange={(e) =>
          onChange({ kind, note: e.target.value, ...(attachmentId !== undefined ? { attachmentId } : {}) })
        }
        helperText={
          kind === 'other'
            ? `Przy „inne” obowiązkowy (co najmniej ${MIN_OTHER_NOTE} znaków)`
            : fileKind
              ? 'Opcjonalnie: strona, pozycja'
              : 'Opcjonalnie: z kim, kiedy'
        }
        slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'pre-order-agreement-source-note' } }}
      />
    </Box>
  );
};

export interface FieldSource {
  kind: string;
  label: string;
  note: string | null;
  by: string | null;
  at: string;
  /** Plik od klienta (26.10.2026) — nazwa i czy usunięty. */
  attachment?: { id: number; filename: string; deleted: boolean } | null;
}

/** „plik od klienta: zam.pdf (usunięty)” — etykieta z nazwą pliku. */
const sourceLabel = (s: FieldSource): string =>
  s.attachment ? `${s.label}: ${s.attachment.filename}${s.attachment.deleted ? ' (usunięty)' : ''}` : s.label;

/**
 * Źródło bieżącej wartości pól przy wierszu karty prezlecenia: „źródło:
 * telefon”, opis i autor w dymku. Kilka pól w wierszu (np. miasto i data
 * załadunku) — każde inne źródło osobno.
 */
export const FieldSourceTags = ({
  sources,
  fields,
}: {
  sources: Record<string, FieldSource> | undefined;
  fields: readonly string[];
}) => {
  const seen = new Set<string>();
  const tags = fields
    .map((f) => sources?.[f])
    .filter((s): s is FieldSource => !!s)
    .filter((s) => {
      const key = `${s.kind}|${s.attachment?.id ?? ''}|${s.note ?? ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  if (tags.length === 0) return null;
  return (
    <>
      {tags.map((s) => (
        <Tooltip
          key={`${s.kind}|${s.attachment?.id ?? ''}|${s.note ?? ''}`}
          title={[s.note, s.by].filter(Boolean).join(' · ') || sourceLabel(s)}
        >
          <Chip
            size="small"
            variant="outlined"
            sx={{ ml: 0.5 }}
            data-testid="pre-order-field-source"
            label={`źródło: ${sourceLabel(s)}${s.note ? ` — ${s.note}` : ''}`}
          />
        </Tooltip>
      ))}
    </>
  );
};
